import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Account, CreateAccount, UpdateAccount, AccountTypeOption, Customer, CreateCustomer, UpdateCustomer, Vendor, CreateVendor, UpdateVendor,
  Product, CreateProduct, UpdateProduct, ExpenseSummary, CreateExpense,
  RefundSummary, CreateRefund, RefundReceiptSummary, CreateRefundReceipt, PaymentTerm,
  Company, CreateCompany, UpdateCompany,
  InvoiceSummary, BillSummary, JournalEntrySummary,
  CreateInvoice, CreateBill, CreateJournalEntry,
  TrialBalanceReport, ProfitLossReport, BalanceSheetReport, GroupedReport, GroupedReportKind, ReportColumns,
  ConsolidatedTrialBalanceReport, ConsolidatedProfitLossReport, ConsolidatedBalanceSheetReport,
  CustomerReceipt, RecordCustomerReceipt, RecordCustomerReceiptOnAccount,
  SupplierPayment, RecordSupplierPayment, RecordSupplierPaymentOnAccount,
  CustomerCreditNoteSummary, VendorCreditNoteSummary,
  CreateCustomerCreditNote, CreateVendorCreditNote,
  VatReturn, VatReturnSummary, VatBoxDetail, VatException, AccountTransactionsReport, AgingDetailRow,
  DashAttentionTask, DashProfitLoss, DashExpenseAccount, DashBankAccount, DashCashFlow, SaveVatReturnRequest, AddVatAdjustmentRequest,
  AgingReport, CustomerBalanceReport, CollectionReport, PaymentDueReport, ChequeRegisterReport,
  AuditLogEntry, TransactionEvent, TransactionEventFilter, CompanyUser, InviteUserRequest, InviteUserResponse,
  Attachment, SmtpSettings, SaveSmtpSettingsRequest, SendInvoiceEmailRequest, EmailSendResult,
  CustomerStatementReport, DashboardSummary, SupplierStatementReport,
  HmrcConnectionStatus, HmrcObligation, HmrcSubmitReturnReceipt, FraudPreventionContext,
  PagedResult, PagedPaymentsResult, InvoiceListRow, BillListRow, ExpenseListRow,
  CustomerCreditNoteListRow, VendorCreditNoteListRow, RefundListRow, RefundReceiptListRow,
  InvoiceLookup, BillLookup, CashMovementRow, RefundDetail, RefundReceiptDetail, DefaultAccount,
  CustomerReceiptListRow, SupplierPaymentListRow,
  GlobalSearchResult
} from './models';
import { PageRequest } from '../../shared/list/server-paged-list';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl;

  /* ---------- Server-side paged lists ---------- */
  private paged<R>(path: string, q: PageRequest): Observable<R> {
    let params = new HttpParams().set('page', q.page).set('pageSize', q.pageSize);
    if (q.search) params = params.set('search', q.search);
    if (q.fromDate) params = params.set('fromDate', q.fromDate);
    if (q.toDate) params = params.set('toDate', q.toDate);
    return this.http.get<R>(`${this.base}/${path}/paged`, { params });
  }
  getInvoicesPaged(q: PageRequest) { return this.paged<PagedResult<InvoiceListRow>>('invoices', q); }
  getBillsPaged(q: PageRequest) { return this.paged<PagedResult<BillListRow>>('bills', q); }
  getJournalEntriesPaged(q: PageRequest) { return this.paged<PagedResult<JournalEntrySummary>>('journalentries', q); }
  getExpensesPaged(q: PageRequest) { return this.paged<PagedResult<ExpenseListRow>>('expenses', q); }
  getCustomersPaged(q: PageRequest) { return this.paged<PagedResult<Customer>>('customers', q); }
  getVendorsPaged(q: PageRequest) { return this.paged<PagedResult<Vendor>>('vendors', q); }
  getCustomerReceiptsPaged(q: PageRequest) { return this.paged<PagedPaymentsResult<CustomerReceiptListRow>>('receipts', q); }
  getSupplierPaymentsPaged(q: PageRequest) { return this.paged<PagedPaymentsResult<SupplierPaymentListRow>>('disbursements', q); }
  getCustomerCreditNotesPaged(q: PageRequest) { return this.paged<PagedResult<CustomerCreditNoteListRow>>('customer-credit-notes', q); }
  getVendorCreditNotesPaged(q: PageRequest) { return this.paged<PagedResult<VendorCreditNoteListRow>>('vendor-credit-notes', q); }
  getRefundsPaged(q: PageRequest) { return this.paged<PagedResult<RefundListRow>>('refunds', q); }
  getRefundReceiptsPaged(q: PageRequest) { return this.paged<PagedResult<RefundReceiptListRow>>('refund-receipts', q); }
  getCashMovementsPaged(q: PageRequest) { return this.paged<PagedResult<CashMovementRow>>('cash-movements', q); }

  getAccounts(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.base}/accounts`);
  }
  createAccount(payload: CreateAccount): Observable<Account> {
    return this.http.post<Account>(`${this.base}/accounts`, payload);
  }
  getAccount(id: number): Observable<Account> {
    return this.http.get<Account>(`${this.base}/accounts/${id}`);
  }
  updateAccount(id: number, payload: UpdateAccount): Observable<Account> {
    return this.http.put<Account>(`${this.base}/accounts/${id}`, payload);
  }
  getAccountTypes(): Observable<AccountTypeOption[]> {
    return this.http.get<AccountTypeOption[]>(`${this.base}/accounts/types`);
  }

  getCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.base}/customers`);
  }
  /** Picker: first customers whose name contains the text. */
  lookupCustomers(search: string, top = 20): Observable<{ id: number; name: string }[]> {
    let params = new HttpParams().set('top', top);
    if (search) params = params.set('search', search);
    return this.http.get<{ id: number; name: string }[]>(`${this.base}/customers/lookup`, { params });
  }
  /** Picker / payment form: first invoices whose number starts with the text (optionally one customer's, open ones only). */
  lookupInvoices(search: string, customerId?: number | null, top = 20, openOnly = false): Observable<InvoiceLookup[]> {
    let params = new HttpParams().set('top', top);
    if (search) params = params.set('search', search);
    if (customerId) params = params.set('customerId', customerId);
    if (openOnly) params = params.set('openOnly', true);
    return this.http.get<InvoiceLookup[]>(`${this.base}/invoices/lookup`, { params });
  }
  /** Picker / payment form: first bills whose number starts with the text (optionally one supplier's, open ones only). */
  lookupBills(search: string, vendorId?: number | null, top = 20, openOnly = false): Observable<BillLookup[]> {
    let params = new HttpParams().set('top', top);
    if (search) params = params.set('search', search);
    if (vendorId) params = params.set('vendorId', vendorId);
    if (openOnly) params = params.set('openOnly', true);
    return this.http.get<BillLookup[]>(`${this.base}/bills/lookup`, { params });
  }
  /** Top bar search: first matches of each kind (number starts with, name contains, or an exact amount). */
  globalSearch(q: string, top = 5): Observable<GlobalSearchResult[]> {
    const params = new HttpParams().set('q', q).set('top', top);
    return this.http.get<GlobalSearchResult[]>(`${this.base}/search`, { params });
  }
  /** Single records for the payment / refund detail pages. */
  getCustomerReceiptRow(id: number): Observable<CustomerReceiptListRow> { return this.http.get<CustomerReceiptListRow>(`${this.base}/receipts/${id}`); }
  getSupplierPaymentRow(id: number): Observable<SupplierPaymentListRow> { return this.http.get<SupplierPaymentListRow>(`${this.base}/disbursements/${id}`); }
  getRefundRow(id: number): Observable<RefundDetail> { return this.http.get<RefundDetail>(`${this.base}/refunds/${id}`); }
  getRefundReceiptRow(id: number): Observable<RefundReceiptDetail> { return this.http.get<RefundReceiptDetail>(`${this.base}/refund-receipts/${id}`); }
  /** The current company's default posting accounts. */
  getDefaultAccounts(): Observable<DefaultAccount[]> {
    return this.http.get<DefaultAccount[]>(`${this.base}/companies/current/default-accounts`);
  }
  setDefaultAccounts(changes: { role: string; accountId: number | null }[]): Observable<DefaultAccount[]> {
    return this.http.put<DefaultAccount[]>(`${this.base}/companies/current/default-accounts`, changes);
  }
  getCustomer(id: number): Observable<Customer> {
    return this.http.get<Customer>(`${this.base}/customers/${id}`);
  }
  createCustomer(payload: CreateCustomer): Observable<Customer> {
    return this.http.post<Customer>(`${this.base}/customers`, payload);
  }
  updateCustomer(id: number, payload: UpdateCustomer): Observable<Customer> {
    return this.http.put<Customer>(`${this.base}/customers/${id}`, payload);
  }
  deleteCustomer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/customers/${id}`);
  }

  getVendors(): Observable<Vendor[]> {
    return this.http.get<Vendor[]>(`${this.base}/vendors`);
  }
  getVendor(id: number): Observable<Vendor> {
    return this.http.get<Vendor>(`${this.base}/vendors/${id}`);
  }
  createVendor(payload: CreateVendor): Observable<Vendor> {
    return this.http.post<Vendor>(`${this.base}/vendors`, payload);
  }
  updateVendor(id: number, payload: UpdateVendor): Observable<Vendor> {
    return this.http.put<Vendor>(`${this.base}/vendors/${id}`, payload);
  }
  deleteVendor(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/vendors/${id}`);
  }

  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.base}/products`);
  }
  getProduct(id: number): Observable<Product> {
    return this.http.get<Product>(`${this.base}/products/${id}`);
  }
  createProduct(payload: CreateProduct): Observable<Product> {
    return this.http.post<Product>(`${this.base}/products`, payload);
  }
  updateProduct(id: number, payload: UpdateProduct): Observable<Product> {
    return this.http.put<Product>(`${this.base}/products/${id}`, payload);
  }

  getPaymentTerms(): Observable<PaymentTerm[]> {
    return this.http.get<PaymentTerm[]>(`${this.base}/payment-terms`);
  }

  getExpenses(): Observable<ExpenseSummary[]> {
    return this.http.get<ExpenseSummary[]>(`${this.base}/expenses`);
  }
  createExpense(payload: CreateExpense): Observable<number> {
    return this.http.post<number>(`${this.base}/expenses`, payload);
  }
  updateExpense(id: number, payload: CreateExpense): Observable<void> {
    return this.http.put<void>(`${this.base}/expenses/${id}`, payload);
  }

  getRefunds(): Observable<RefundSummary[]> {
    return this.http.get<RefundSummary[]>(`${this.base}/refunds`);
  }
  createRefund(payload: CreateRefund): Observable<number> {
    return this.http.post<number>(`${this.base}/refunds`, payload);
  }
  getRefundReceipts(): Observable<RefundReceiptSummary[]> {
    return this.http.get<RefundReceiptSummary[]>(`${this.base}/refund-receipts`);
  }
  createRefundReceipt(payload: CreateRefundReceipt): Observable<number> {
    return this.http.post<number>(`${this.base}/refund-receipts`, payload);
  }

  getInvoices(): Observable<InvoiceSummary[]> {
    return this.http.get<InvoiceSummary[]>(`${this.base}/invoices`);
  }
  getInvoiceById(id: number): Observable<any> {
    return this.http.get(`${this.base}/invoices/${id}`);
  }

  createInvoice(payload: CreateInvoice): Observable<number> {
    return this.http.post<number>(`${this.base}/invoices`, payload);
  }
  /** Edits an invoice; the server re-posts it (payments stay linked). */
  updateInvoice(id: number, payload: CreateInvoice): Observable<void> {
    return this.http.put<void>(`${this.base}/invoices/${id}`, payload);
  }

  getBills(): Observable<BillSummary[]> {
    return this.http.get<BillSummary[]>(`${this.base}/bills`);
  }
  getBillById(id: number): Observable<any> {
    return this.http.get(`${this.base}/bills/${id}`);
  }

  createBill(payload: CreateBill): Observable<number> {
    return this.http.post<number>(`${this.base}/bills`, payload);
  }
  updateBill(id: number, payload: CreateBill): Observable<void> {
    return this.http.put<void>(`${this.base}/bills/${id}`, payload);
  }

  getJournalEntries(): Observable<JournalEntrySummary[]> {
    return this.http.get<JournalEntrySummary[]>(`${this.base}/journalentries`);
  }
  getJournalEntryById(id: number): Observable<any> {
    return this.http.get(`${this.base}/journalentries/${id}`);
  }

  createJournalEntry(payload: CreateJournalEntry): Observable<number> {
    return this.http.post<number>(`${this.base}/journalentries`, payload);
  }
  /** Edits a manual journal entry (document postings are refused by the server). */
  updateJournalEntry(id: number, payload: CreateJournalEntry): Observable<void> {
    return this.http.put<void>(`${this.base}/journalentries/${id}`, payload);
  }

  getExpenseById(id: number): Observable<any> {
    return this.http.get(`${this.base}/expenses/${id}`);
  }
  getCustomerCreditNoteById(id: number): Observable<any> {
    return this.http.get(`${this.base}/customer-credit-notes/${id}`);
  }
  getVendorCreditNoteById(id: number): Observable<any> {
    return this.http.get(`${this.base}/vendor-credit-notes/${id}`);
  }

  getTrialBalance(asOfDate?: string): Observable<TrialBalanceReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<TrialBalanceReport>(`${this.base}/reports/trial-balance`, { params });
  }

  /** The transactions behind an account's figure (no fromDate = start of that financial year). */
  getAccountTransactions(code: string, toDate: string, fromDate?: string | null, page = 1, pageSize = 100): Observable<AccountTransactionsReport> {
    let params = new HttpParams().set('code', code).set('toDate', toDate).set('page', page).set('pageSize', pageSize);
    if (fromDate) params = params.set('fromDate', fromDate);
    return this.http.get<AccountTransactionsReport>(`${this.base}/reports/account-transactions`, { params });
  }
  /** The open invoices (AR) / bills (AP) behind an aging figure. */
  getAgingDetail(kind: 'AR' | 'AP', asOfDate: string, partyId?: number | null, bucket?: number | null): Observable<AgingDetailRow[]> {
    let params = new HttpParams().set('kind', kind).set('asOfDate', asOfDate);
    if (partyId) params = params.set('partyId', partyId);
    if (bucket !== null && bucket !== undefined) params = params.set('bucket', bucket);
    return this.http.get<AgingDetailRow[]>(`${this.base}/reports/aging-detail`, { params });
  }
  // ---- Dashboard cards ----
  getDashAttention(): Observable<DashAttentionTask[]> {
    return this.http.get<DashAttentionTask[]>(`${this.base}/dashboard/attention`);
  }
  getDashProfitLoss(from: string, to: string, prevFrom: string, prevTo: string): Observable<DashProfitLoss> {
    const params = new HttpParams().set('from', from).set('to', to).set('prevFrom', prevFrom).set('prevTo', prevTo);
    return this.http.get<DashProfitLoss>(`${this.base}/dashboard/profit-loss`, { params });
  }
  getDashExpenses(from: string, to: string, prevFrom: string, prevTo: string): Observable<DashExpenseAccount[]> {
    const params = new HttpParams().set('from', from).set('to', to).set('prevFrom', prevFrom).set('prevTo', prevTo);
    return this.http.get<DashExpenseAccount[]>(`${this.base}/dashboard/expenses`, { params });
  }
  getDashBankAccounts(): Observable<DashBankAccount[]> {
    return this.http.get<DashBankAccount[]>(`${this.base}/dashboard/bank-accounts`);
  }
  getDashCashFlow(from: string, to: string): Observable<DashCashFlow> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<DashCashFlow>(`${this.base}/dashboard/cash-flow`, { params });
  }
  getProfitLoss(fromDate?: string, toDate?: string): Observable<ProfitLossReport> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<ProfitLossReport>(`${this.base}/reports/profit-loss`, { params });
  }

  /** QuickBooks-style grouped TB / P&L / balance sheet. bs / tb: balances at toDate (or at each period end from fromDate). */
  getGroupedReport(report: GroupedReportKind, fromDate: string, toDate: string, columns: ReportColumns): Observable<GroupedReport> {
    const params = new HttpParams().set('report', report).set('fromDate', fromDate).set('toDate', toDate).set('columns', columns);
    return this.http.get<GroupedReport>(`${this.base}/reports/grouped`, { params });
  }

  getBalanceSheet(asOfDate?: string): Observable<BalanceSheetReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<BalanceSheetReport>(`${this.base}/reports/balance-sheet`, { params });
  }

  getConsolidatedTrialBalance(asOfDate?: string): Observable<ConsolidatedTrialBalanceReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<ConsolidatedTrialBalanceReport>(`${this.base}/reports/consolidated/trial-balance`, { params });
  }

  getConsolidatedProfitLoss(fromDate?: string, toDate?: string): Observable<ConsolidatedProfitLossReport> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<ConsolidatedProfitLossReport>(`${this.base}/reports/consolidated/profit-loss`, { params });
  }

  getConsolidatedBalanceSheet(asOfDate?: string): Observable<ConsolidatedBalanceSheetReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<ConsolidatedBalanceSheetReport>(`${this.base}/reports/consolidated/balance-sheet`, { params });
  }

  getCustomerReceipts(): Observable<CustomerReceipt[]> {
    return this.http.get<CustomerReceipt[]>(`${this.base}/receipts`);
  }

  recordCustomerReceipt(payload: RecordCustomerReceipt): Observable<CustomerReceipt> {
    return this.http.post<CustomerReceipt>(`${this.base}/receipts`, payload);
  }

  recordCustomerReceiptOnAccount(payload: RecordCustomerReceiptOnAccount): Observable<CustomerReceipt> {
    return this.http.post<CustomerReceipt>(`${this.base}/receipts/on-account`, payload);
  }

  getSupplierPayments(): Observable<SupplierPayment[]> {
    return this.http.get<SupplierPayment[]>(`${this.base}/disbursements`);
  }

  recordSupplierPayment(payload: RecordSupplierPayment): Observable<SupplierPayment> {
    return this.http.post<SupplierPayment>(`${this.base}/disbursements`, payload);
  }

  recordSupplierPaymentOnAccount(payload: RecordSupplierPaymentOnAccount): Observable<SupplierPayment> {
    return this.http.post<SupplierPayment>(`${this.base}/disbursements/on-account`, payload);
  }

  getMyCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.base}/companies`);
  }

  createCompany(payload: CreateCompany): Observable<Company> {
    return this.http.post<Company>(`${this.base}/companies`, payload);
  }

  getCompany(id: number): Observable<Company> {
    return this.http.get<Company>(`${this.base}/companies/${id}`);
  }

  updateCompany(id: number, payload: UpdateCompany): Observable<Company> {
    return this.http.put<Company>(`${this.base}/companies/${id}`, payload);
  }

  getCustomerCreditNotes(): Observable<CustomerCreditNoteSummary[]> {
    return this.http.get<CustomerCreditNoteSummary[]>(`${this.base}/customer-credit-notes`);
  }

  createCustomerCreditNote(payload: CreateCustomerCreditNote): Observable<number> {
    return this.http.post<number>(`${this.base}/customer-credit-notes`, payload);
  }
  updateCustomerCreditNote(id: number, payload: CreateCustomerCreditNote): Observable<void> {
    return this.http.put<void>(`${this.base}/customer-credit-notes/${id}`, payload);
  }

  getVendorCreditNotes(): Observable<VendorCreditNoteSummary[]> {
    return this.http.get<VendorCreditNoteSummary[]>(`${this.base}/vendor-credit-notes`);
  }

  createVendorCreditNote(payload: CreateVendorCreditNote): Observable<number> {
    return this.http.post<number>(`${this.base}/vendor-credit-notes`, payload);
  }
  updateVendorCreditNote(id: number, payload: CreateVendorCreditNote): Observable<void> {
    return this.http.put<void>(`${this.base}/vendor-credit-notes/${id}`, payload);
  }

  computeVatReturn(periodStart: string, periodEnd: string): Observable<VatReturn> {
    const params = new HttpParams().set('periodStart', periodStart).set('periodEnd', periodEnd);
    return this.http.get<VatReturn>(`${this.base}/vat/compute`, { params });
  }
  /** The documents behind one VAT100 box (1, 2, 4, 6, 7, 8, 9). */
  getVatBoxDetail(periodStart: string, periodEnd: string, box: number, page = 1, pageSize = 100): Observable<VatBoxDetail> {
    const params = new HttpParams().set('periodStart', periodStart).set('periodEnd', periodEnd)
      .set('page', page).set('pageSize', pageSize);
    return this.http.get<VatBoxDetail>(`${this.base}/vat/compute/box/${box}`, { params });
  }
  getVatReturns(): Observable<VatReturnSummary[]> {
    return this.http.get<VatReturnSummary[]>(`${this.base}/vat/returns`);
  }
  /** VAT exception report: changes to VAT transactions in filed periods made after filing. */
  getVatExceptions(vatReturnId?: number | null): Observable<VatException[]> {
    let params = new HttpParams();
    if (vatReturnId) params = params.set('vatReturnId', vatReturnId);
    return this.http.get<VatException[]>(`${this.base}/vat/exceptions`, { params });
  }
  /** Records every calendar quarter ending on or before `through` as filed (Admin). */
  markVatFiledThrough(through: string): Observable<{ marked: number }> {
    return this.http.post<{ marked: number }>(`${this.base}/vat/returns/mark-filed`, { through });
  }
  getVatReturn(id: number): Observable<VatReturn> {
    return this.http.get<VatReturn>(`${this.base}/vat/returns/${id}`);
  }
  saveVatReturn(payload: SaveVatReturnRequest): Observable<number> {
    return this.http.post<number>(`${this.base}/vat/returns`, payload);
  }
  fileVatReturn(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/vat/returns/${id}/file`, {});
  }
  addVatAdjustment(id: number, payload: AddVatAdjustmentRequest): Observable<number> {
    return this.http.post<number>(`${this.base}/vat/returns/${id}/adjustments`, payload);
  }
  removeVatAdjustment(id: number, adjId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/vat/returns/${id}/adjustments/${adjId}`);
  }

  getArAging(asOfDate?: string): Observable<AgingReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<AgingReport>(`${this.base}/reports/ar-aging`, { params });
  }
  getApAging(asOfDate?: string): Observable<AgingReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<AgingReport>(`${this.base}/reports/ap-aging`, { params });
  }
  getCustomerBalances(asOfDate?: string): Observable<CustomerBalanceReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<CustomerBalanceReport>(`${this.base}/reports/customer-balances`, { params });
  }
  getCollectionReport(asOfDate?: string): Observable<CollectionReport> {
    let params = new HttpParams();
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<CollectionReport>(`${this.base}/reports/collection`, { params });
  }
  getPaymentDueReport(asOfDate?: string, aheadDays = 14): Observable<PaymentDueReport> {
    let params = new HttpParams().set('aheadDays', aheadDays);
    if (asOfDate) params = params.set('asOfDate', asOfDate);
    return this.http.get<PaymentDueReport>(`${this.base}/reports/payment-due`, { params });
  }
  getChequeRegister(fromDate?: string, toDate?: string, direction?: number): Observable<ChequeRegisterReport> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (direction !== undefined && direction !== null) params = params.set('direction', direction);
    return this.http.get<ChequeRegisterReport>(`${this.base}/reports/cheque-register`, { params });
  }

  getDashboardSummary(): Observable<DashboardSummary> {
    return this.http.get<DashboardSummary>(`${this.base}/dashboard/summary`);
  }

  getCustomerStatement(customerId: number, fromDate?: string, toDate?: string): Observable<CustomerStatementReport> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<CustomerStatementReport>(`${this.base}/reports/customer-statement/${customerId}`, { params });
  }

  getSupplierStatement(vendorId: number, fromDate?: string, toDate?: string): Observable<SupplierStatementReport> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<SupplierStatementReport>(`${this.base}/reports/supplier-statement/${vendorId}`, { params });
  }

  getAuditLog(take = 100, beforeId?: number): Observable<AuditLogEntry[]> {
    let params = new HttpParams().set('take', take);
    if (beforeId) params = params.set('beforeId', beforeId);
    return this.http.get<AuditLogEntry[]>(`${this.base}/audit`, { params });
  }

  getTransactionEvents(filter: TransactionEventFilter = {}): Observable<TransactionEvent[]> {
    let params = new HttpParams();
    if (filter.take) params = params.set('take', filter.take);
    if (filter.beforeId) params = params.set('beforeId', filter.beforeId);
    if (filter.entityType) params = params.set('entityType', filter.entityType);
    if (filter.eventType) params = params.set('eventType', filter.eventType);
    if (filter.userId) params = params.set('userId', filter.userId);
    if (filter.fromDate) params = params.set('fromDate', filter.fromDate);
    if (filter.toDate) params = params.set('toDate', filter.toDate);
    if (filter.search) params = params.set('search', filter.search);
    return this.http.get<TransactionEvent[]>(`${this.base}/transactions`, { params });
  }

  getTransactionEvent(id: number): Observable<TransactionEvent> {
    return this.http.get<TransactionEvent>(`${this.base}/transactions/${id}`);
  }

  getCompanyUsers(): Observable<CompanyUser[]> {
    return this.http.get<CompanyUser[]>(`${this.base}/users`);
  }
  inviteUser(payload: InviteUserRequest): Observable<InviteUserResponse> {
    return this.http.post<InviteUserResponse>(`${this.base}/users`, payload);
  }
  updateUserRole(userId: number, role: string): Observable<void> {
    return this.http.put<void>(`${this.base}/users/${userId}/role`, { role });
  }
  removeCompanyUser(userId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}`);
  }

  getAttachments(entityType: string, entityId: number): Observable<Attachment[]> {
    const params = new HttpParams().set('entityType', entityType).set('entityId', entityId);
    return this.http.get<Attachment[]>(`${this.base}/attachments`, { params });
  }
  uploadAttachment(entityType: string, entityId: number, file: File): Observable<Attachment> {
    const form = new FormData();
    form.append('entityType', entityType);
    form.append('entityId', String(entityId));
    form.append('file', file);
    return this.http.post<Attachment>(`${this.base}/attachments`, form);
  }
  downloadAttachmentUrl(id: number): string {
    return `${this.base}/attachments/${id}/download`;
  }
  deleteAttachment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/attachments/${id}`);
  }

  getSmtpSettings(): Observable<SmtpSettings | null> {
    return this.http.get<SmtpSettings | null>(`${this.base}/email/settings`);
  }
  saveSmtpSettings(payload: SaveSmtpSettingsRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/email/settings`, payload);
  }
  sendInvoiceEmail(invoiceId: number, payload: SendInvoiceEmailRequest): Observable<EmailSendResult> {
    return this.http.post<EmailSendResult>(`${this.base}/email/invoice/${invoiceId}`, payload);
  }

  // ----- HMRC MTD -----
  getHmrcConnectUrl(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.base}/hmrc/connect`);
  }
  getHmrcStatus(): Observable<HmrcConnectionStatus> {
    return this.http.get<HmrcConnectionStatus>(`${this.base}/hmrc/status`);
  }
  disconnectHmrc(): Observable<void> {
    return this.http.post<void>(`${this.base}/hmrc/disconnect`, {});
  }
  getHmrcObligations(fromDate: string, toDate: string, status: 'O' | 'F' | null, context: FraudPreventionContext): Observable<HmrcObligation[]> {
    return this.http.post<HmrcObligation[]>(`${this.base}/hmrc/vat/obligations`,
      { from: fromDate, to: toDate, status, context });
  }
  submitVatReturnToHmrc(vatReturnId: number, periodKey: string, context: FraudPreventionContext): Observable<HmrcSubmitReturnReceipt> {
    return this.http.post<HmrcSubmitReturnReceipt>(`${this.base}/hmrc/vat/submit`,
      { vatReturnId, periodKey, context });
  }
}
